import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  generateSettlement,
  getSettlement,
  saveSettlementItems,
  saveSettlementRemark,
  confirmSettlement,
  reopenSettlement,
  toggleTaxInvoice,
  listSettlement,
  listCustomers,
  listChannelsByFlag,
  generateMissingSettlements,
  paySettlementByCard,
  markSettlementDeposit,
  unmarkSettlementPaid,
  listFlagStyles,
  listOneTimeFeeChannels,
  regenerateAllSettlements,
  listSettlementHistory,
} from '../../api/system/system.api';
import SettlementHistoryTable from './SettlementHistoryTable';
import BillingNoticePanel from './BillingNoticePanel';
import { flagBadgeStyle, toFlagColorMap } from '../../libs/flagStyle';
// 청구월 옵션은 '전체 고객'·'고객사 상세'와 같은 목록을 쓴다(같은 목록을 두 벌 두지 않는다).
// ⚠️ 이 화면의 기본값은 **전월**이라 기준월 상태 자체는 공유하지 않는다 — libs/baseMonth.js 머리주석 참고.
import { monthOptions } from '../../libs/baseMonth';
import { isBillableForMonth } from '../../libs/subscriptionPeriod';

/** 'YYYY-MM' 의 전월. 정산 이력에서 전월 행을 찾을 때 쓴다. */
const prevMonthOf = (ym) => {
  const [y, m] = String(ym ?? '').split('-').map(Number);
  if (!y || !m) return '';
  return m === 1 ? `${y - 1}-12` : `${y}-${String(m - 1).padStart(2, '0')}`;
};

const won = (n) => (n ?? 0).toLocaleString('ko-KR');

/** 'YYYY-MM-DD HH:mm' (초·타임존 제거). 서버는 OffsetDateTime.toString() 을 그대로 내려준다. */
const dateTimeText = (iso) => {
  if (!iso) return '';
  const t = String(iso).replace('T', ' ');
  return t.length >= 16 ? t.slice(0, 16) : t;
};

/**
 * 화면에 보여줄 오류 문구.
 *
 * 정산 API 는 실패 사유를 `CommonResponse.data`(문자열)로 내려주고, `system.api.js` 의 `settlementResult` 가
 * 그것을 `Error.message` 로 바꿔 던진다. 두 형태를 모두 받아 사람이 읽을 한 줄로 만든다.
 */
const errorText = (e) =>
  e?.response?.data?.data || e?.message || String(e ?? '알 수 없는 오류');

const STATUS_BADGE = {
  DRAFT: 'bg-yellow-100 text-yellow-800',
  CONFIRMED: 'bg-green-100 text-green-800',
  AUTO_CONFIRMED: 'bg-green-100 text-green-800',
  CANCELED: 'bg-gray-200 text-gray-600',
};

// 정산 상태 국문 표기. 화면에 영문 코드가 그대로 노출되던 것을 통일한다(2026-08-04).
// 'NONE' 은 정산서가 아직 없는 상태로, 서버 status 가 아니라 이 화면이 만들어 넣는 값이다.
const STATUS_LABEL = {
  NONE: '미생성',
  DRAFT: '초안',
  CONFIRMED: '확정',
  AUTO_CONFIRMED: '확정(자동)',
  CANCELED: '취소',
};
const statusLabel = (s) => STATUS_LABEL[s] ?? s;

const isConfirmed = (st) => st === 'CONFIRMED' || st === 'AUTO_CONFIRMED';
const isPaid = (r) => !!r.paidAt;

/**
 * 행의 수납 상태(필터·표시 공용). 미확정이면 null — 아직 수납 단계에 들어가지 않은 상태다.
 * 순수 함수라 모듈 스코프에 둔다(컴포넌트 안에 두면 매 렌더 새로 만들어져 useMemo 의존성이 흔들린다).
 */
const payStateOf = (r) => {
  if (isPaid(r)) return 'PAID';
  if (!isConfirmed(r.status)) return null;
  return r.lastPaymentFailedAt ? 'FAILED' : 'UNPAID';
};

/**
 * 정산 상태 필터 항목. 값은 목록 행의 `status` 그대로(미생성은 이 화면이 만드는 'NONE').
 * 배지 색은 목록의 '정산 상태' 칸과 같게 맞춘다.
 */
const STATUS_FILTERS = [
  { value: 'NONE', label: '미생성', badge: 'bg-red-100 text-red-700' },
  { value: 'DRAFT', label: '초안', badge: STATUS_BADGE.DRAFT },
  { value: 'CONFIRMED', label: '확정', badge: STATUS_BADGE.CONFIRMED },
  { value: 'AUTO_CONFIRMED', label: '확정(자동)', badge: STATUS_BADGE.AUTO_CONFIRMED },
  { value: 'CANCELED', label: '취소', badge: STATUS_BADGE.CANCELED },
];

/** '미확정만' 프리셋 = 미생성 + 초안. 종전 '미확정만 보기' 체크박스를 한 번 누르기로 대체한다. */
const PENDING_STATUSES = ['NONE', 'DRAFT'];

/**
 * 수납 상태 필터 항목. **확정된 건에만 있는 상태**다(미확정은 '미확정만 보기'로 조회).
 * 배지 색은 목록의 '수납 상태' 칸과 같게 맞춘다 — 색이 다르면 같은 상태인지 알기 어렵다.
 */
const PAY_FILTERS = [
  { value: 'PAID', label: '수납 완료', badge: 'bg-blue-100 text-blue-800' },
  { value: 'UNPAID', label: '미수금', badge: 'bg-amber-100 text-amber-700' },
  { value: 'FAILED', label: '결제 실패', badge: 'bg-red-100 text-red-700 font-semibold' },
];

/**
 * 후불 항목 종류 → 사람이 읽는 이름. 조기 확정 확인 문구에서 "무엇이 빠질 수 있는지" 를 적을 때 쓴다.
 * 🔴 **모르는 코드는 코드 그대로 보여준다** — 조용히 빼면 정말 늘어나는 항목을 담당자가 못 본다.
 */
const POSTPAID_CATEGORY_LABEL = {
  MONTHLY: '월정액',
  ACQUISITION: '가입(획득) 과금',
  MESSAGE: '메시지 발송',
  MOMENT: '카카오 모먼트',
  GIFT: '카카오 선물하기',
  ETC: '기타',
};

/**
 * 구독이 없는데 목록에 있는 이유를 행에 적어 둔다(2026-08-31).
 *
 * 담당자가 "구독도 없는 업체가 왜 정산 목록에 있지?" 로 멈추지 않게 하는 것이 목적이다.
 * `SUB` 는 정상 경로라 배지를 붙이지 않는다 — 대부분의 행에 붙는 배지는 정보가 아니라 소음이다.
 */
const BASIS_BADGE = {
  ONE_TIME: {
    label: '1회성만',
    className: 'bg-purple-100 text-purple-700',
    title: '이 달 활성 구독은 없지만 1회성 비용(설치비·스티커 등)이 이 달로 잡혀 있어 표시됩니다.',
  },
  SETTLEMENT: {
    label: '구독 없음',
    className: 'bg-gray-200 text-gray-700',
    title: '이 달 활성 구독도 1회성 비용도 없지만 이 달 정산서가 이미 있어 표시됩니다(구독 종료 후 잔여 청구 등).',
  },
};

// onOpenDetail: 작업 목록에서 '고객사' 버튼 → 전체 고객 > 고객사 상세로 이동(A2 화면 재편).
export default function SettlementAdmin({ onOpenDetail }) {
  const months = useMemo(() => monthOptions(), []);
  const [channelId, setChannelId] = useState('');
  const [month, setMonth] = useState(months[1] || months[0]);
  const [settlement, setSettlement] = useState(null);
  const [items, setItems] = useState([]);
  const [customerRemark, setCustomerRemark] = useState('');
  const [adminMemo, setAdminMemo] = useState('');
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState('');
  // 안내와 오류를 같은 자리에 회색 박스로 보여주다 보니 결제 실패가 눈에 띄지 않았다 → 오류는 빨간 배너로 구분.
  const [msgError, setMsgError] = useState(false);

  /**
   * '결제 실패' 배지 호버 툴팁. `{ row, top, left, flip }`.
   *
   * 왜 네이티브 `title` 을 쓰지 않는가(2026-08-06): 브라우저가 툴팁을 띄우지 않는 환경이 있었다
   * (커서만 `cursor-help` 로 바뀌고 내용이 안 나옴). 표시 여부를 브라우저에 맡기지 않고 직접 그린다.
   *
   * ⚠️ **`position: fixed` 여야 한다.** 목록은 `max-h-[30rem] overflow-y-auto` + `overflow-x-auto`
   *    스크롤 컨테이너 안에 있어서 `absolute` 로 띄우면 컨테이너 경계에서 잘린다.
   *    그래서 마우스가 올라온 순간의 화면 좌표(`getBoundingClientRect`)를 받아 화면 기준으로 배치한다.
   */
  const [failTip, setFailTip] = useState(null);

  const TIP_WIDTH = 340;   // max-w 와 맞춘 값(좌우 클램프 계산용)
  const TIP_HEIGHT = 132;  // 대략치. 화면 아래쪽이면 위로 뒤집기 위한 판단에만 쓴다.

  const showFailTip = (e, row) => {
    const rect = e.currentTarget.getBoundingClientRect();
    // 화면 오른쪽/아래쪽으로 넘치면 안쪽으로 접어 넣는다.
    const left = Math.max(8, Math.min(rect.left, window.innerWidth - TIP_WIDTH - 8));
    const flip = rect.bottom + TIP_HEIGHT > window.innerHeight;
    setFailTip({ row, left, top: flip ? rect.top - 8 : rect.bottom + 8, flip });
  };

  // 열린 정산서 채널의 정산 이력(전월 금액 비교용, 2026-10-01). 고객사 상세의 '정산 이력' 과 같은 표를 쓴다.
  //   계약상 구독 항목을 0원으로 넣어야 하는 경우 등, 이번 달 금액이 맞는지 전월과 견줘 빠르게 판단하려는 것.
  const [history, setHistory] = useState([]);
  const [historyErr, setHistoryErr] = useState('');
  const historyChannelId = settlement?.channelId ?? null;
  useEffect(() => {
    if (!historyChannelId) { setHistory([]); setHistoryErr(''); return undefined; }
    let canceled = false;
    // 정산서가 바뀔 때마다(열기·재생성·저장·확정) 다시 부른다 — 이력의 '이번 달' 행 금액·상태도 따라 바뀌어야 한다.
    listSettlementHistory(historyChannelId)
      .then((r) => { if (!canceled) { setHistory(r?.data ?? []); setHistoryErr(''); } })
      .catch((e) => { if (!canceled) { setHistory([]); setHistoryErr(errorText(e)); } });
    return () => { canceled = true; };
  }, [historyChannelId, settlement]);

  const apply = (s) => {
    setSettlement(s);
    setItems(s?.items ? s.items.map((it) => ({ ...it })) : []);
    setCustomerRemark(s?.customerRemark || '');
    setAdminMemo(s?.adminMemo || '');
  };

  const run = async (fn, okMsg) => {
    if (!channelId) { setMsg('channelId를 입력하세요.'); return; }
    setLoading(true);
    setMsg('');
    setMsgError(false);
    try {
      const result = await fn();
      // 🔴 `fn` 이 **false** 를 돌려주면 '사람이 취소했다' 는 뜻이다 — 성공 메시지를 띄우지 않는다.
      //    종전에는 `fn` 이 확인창 거절로 그냥 `return` 해도 여기서 okMsg 를 덮어써서
      //    **'확정 완료' 라고 표시됐다**(취소했는데 완료로 보이는 조용한 실패). 2026-08-31 수정.
      if (result !== false && okMsg) setMsg(okMsg);
    } catch (e) {
      setMsg(`오류: ${errorText(e)}`);
      setMsgError(true);
    } finally {
      setLoading(false);
    }
  };

  const doGenerate = () => run(async () => {
    const resp = await generateSettlement(channelId, month);
    apply(resp?.data ?? null);
  }, '초안 생성/재생성 완료');

  const doSaveItems = () => run(async () => {
    await saveSettlementItems(settlement.id, items.map((it) => ({
      category: it.category,
      itemType: it.itemType,
      name: it.name,
      description: it.description ?? null,
      unitPriceNet: Number(it.unitPriceNet) || 0,
      quantity: Number(it.quantity) || 0,
      supplyAmountNet: Number(it.supplyAmountNet) || 0,
      vatExempt: !!it.vatExempt,
      coverageMonth: it.coverageMonth || month,
      billingMode: it.billingMode || 'POSTPAID',
    })));
    const resp = await getSettlement(channelId, month);
    apply(resp?.data ?? null);
  }, '항목 저장(서버 합계 재계산) 완료');

  const doSaveRemark = () => run(async () => {
    await saveSettlementRemark(settlement.id, customerRemark, adminMemo);
  }, '비고 저장 완료');

  /**
   * 확정. **비고를 함께 저장한다.**
   *
   * 종전에는 '비고 저장'이 별도 버튼이라, 고객 노출 비고를 적어 놓고 저장을 안 누른 채 확정하면
   * 그 내용이 조용히 사라졌다(고객 고지서에 아무것도 안 나옴). 확정은 되돌리기 번거로운 작업이므로
   * 여기서 함께 저장하고, 무엇이 저장되는지 확인까지 받는다.
   */
  const doConfirm = () => run(async () => {
    const remarkChanged = customerRemark !== (settlement?.customerRemark || '')
      || adminMemo !== (settlement?.adminMemo || '');

    const lines = [`${month} 정산서를 확정합니다.`, '', `청구 총액: ${won(settlement.total)}원`];
    if (customerRemark.trim()) {
      lines.push('', '고객에게 노출될 비고:', customerRemark.trim());
    } else {
      lines.push('', '고객 노출 비고: (없음)');
    }
    if (remarkChanged) lines.push('', '※ 저장하지 않은 비고 변경분이 함께 저장됩니다.');
    lines.push('', '확정 후에는 금액이 동결됩니다. 진행할까요?');
    if (!window.confirm(lines.join('\n'))) return false; // false = 취소(성공 메시지 금지)

    if (remarkChanged) await saveSettlementRemark(settlement.id, customerRemark, adminMemo);

    // 1차 시도. 이용월이 끝나기 전이면 서버가 **아무것도 바꾸지 않고** 판단 근거만 돌려준다.
    let res = await confirmSettlement(settlement.id);
    if (res?.data?.requiresEarlyCloseAck) {
      const info = res.data;
      const cats = (info.postpaidCategories ?? [])
        .map((c) => POSTPAID_CATEGORY_LABEL[c] ?? c)
        .join(', ');
      const warn = [
        `${month} 정산서를 지금 확정합니다.`,
        '',
        `⚠️ 아직 ${info.confirmableFrom} 이 되지 않았습니다 — ${info.basisMonth} 이용 기간이 끝나지 않았습니다.`,
        '지금 확정하면 남은 기간에 발생하는 사용량이 이 정산서에 반영되지 않습니다.',
      ];
      if (cats) warn.push('', `반영되지 않을 수 있는 항목: ${cats}`);
      warn.push(
        '',
        '(월정액처럼 금액이 이미 정해진 항목은 영향이 없습니다. 발송량·가입수처럼 늘어나는 항목만 해당합니다.)',
        '',
        `확정 후 발생한 사용량을 청구하려면 '재오픈' → 정산서 재생성이 필요합니다.`,
        '',
        '그래도 지금 확정할까요?',
      );
      // 동의하지 않으면 **여기서 멈춘다.** 비고는 이미 저장됐지만 확정은 일어나지 않는다.
      if (!window.confirm(warn.join('\n'))) {
        setMsg('확정하지 않았습니다(비고 변경분은 저장됨).');
        setMsgError(false);
        return false; // false = 취소. 이게 없으면 run() 이 '확정 완료' 로 덮어쓴다.
      }
      res = await confirmSettlement(settlement.id, true);
    }
    // 🔴 확정되지 않았는데 성공 메시지를 띄우면 조용한 실패가 된다.
    if (!res?.data?.confirmed) throw new Error('정산서가 확정되지 않았습니다. 화면을 새로고침해 상태를 확인해 주세요.');

    const resp = await getSettlement(channelId, month);
    apply(resp?.data ?? null);
  }, '확정 완료(비고 포함 저장됨)');

  const doReopen = () => run(async () => {
    await reopenSettlement(settlement.id);
    const resp = await getSettlement(channelId, month);
    apply(resp?.data ?? null);
  }, '재오픈 완료');

  // 채널 설정(is_tax_invoice) + 그 달 DRAFT 정산서의 billing_method 를 함께 바꾼다.
  // month 를 넘기지 않으면 정산서 스냅샷이 안 바뀌어 화면이 '카드 결제'로 남는다.
  const doToggleTax = (val) => run(async () => {
    await toggleTaxInvoice(Number(channelId), val, month);
    const resp = await getSettlement(channelId, month);
    apply(resp?.data ?? null);
  }, val ? '세금계산서 발행 업체로 지정했습니다.' : '카드 결제 업체로 변경했습니다.');

  const editItem = (idx, field, value) => {
    setItems((prev) => prev.map((it, i) => {
      if (i !== idx) return it;
      const next = { ...it, [field]: value };
      if (field === 'unitPriceNet' || field === 'quantity') {
        next.supplyAmountNet = (Number(next.unitPriceNet) || 0) * (Number(next.quantity) || 0);
      }
      return next;
    }));
  };

  const addItem = () => setItems((prev) => [...prev, {
    category: 'ETC', itemType: 'ETC', name: '', unitPriceNet: 0, quantity: 0,
    supplyAmountNet: 0, vatExempt: false, coverageMonth: month, billingMode: 'POSTPAID', isManual: true,
  }]);

  const removeItem = (idx) => setItems((prev) => prev.filter((_, i) => i !== idx));

  // 합계 미리보기(클라). 저장 시 서버가 권위 재계산.
  const preview = useMemo(() => {
    const supplyAll = items.reduce((a, it) => a + (Number(it.supplyAmountNet) || 0), 0);
    const taxable = items.filter((it) => !it.vatExempt).reduce((a, it) => a + (Number(it.supplyAmountNet) || 0), 0);
    const vat = Math.floor(taxable / 10);
    return { supplyAll, taxable, vat, total: supplyAll + vat };
  }, [items]);

  // 메시지 발송 수량 요약(2026-10-01) — '통합 비용 및 성과 대시보드' 와 대조하는 속도를 올리려고 둔다.
  // 항목명은 정산 초안이 고정으로 붙이는 이름(SettlementService.assemble)으로 구분하고, 그 밖의 MESSAGE 항목은
  // 1회성 캠페인(캠페인명이 항목명)으로 본다. 수기로 넣은 MESSAGE 항목도 같은 규칙으로 합산된다.
  const messageQty = useMemo(() => {
    // '기타 메시지' = send_trigger 가 적립·테스트가 아닌 나머지 발송(2026-10-01, 서버 RemainderMessageLabel). 여기 없으면
    // 아래 규칙상 '1회성 캠페인' 으로 잘못 합산된다 — 서버 항목명을 추가하면 이 표도 같이 고칠 것.
    const FIXED = { '자동화 메시지': '자동화', '혜택 메시지': '가입 혜택', '적립 메시지': '적립', '테스트 발송': '테스트', '기타 메시지': '기타' };
    const order = ['1회성 캠페인', '자동화', '가입 혜택', '적립', '테스트', '기타', '모먼트(비회원)'];
    const by = {};
    let total = 0;
    let acquisition = 0;
    items.forEach((it) => {
      const q = Number(it.quantity) || 0;
      if (it.category === 'MESSAGE' || it.category === 'MOMENT') {
        const key = it.category === 'MOMENT' ? '모먼트(비회원)' : (FIXED[it.name] ?? '1회성 캠페인');
        by[key] = (by[key] ?? 0) + q;
        total += q;
      } else if (it.category === 'ACQUISITION') {
        acquisition += q;
      }
    });
    return { total, acquisition, parts: order.filter((k) => by[k]).map((k) => [k, by[k]]) };
  }, [items]);

  // 일할 계산된 항목(coverageFrom~coverageTo 존재 = 개시월 일할). 담당자가 '고객 노출 비고'에 남길 수 있도록 노출.
  const proratedItems = useMemo(
    () => items.filter((it) => it.coverageFrom && it.coverageTo),
    [items],
  );
  const prorationNote = useMemo(
    () => proratedItems.map((it) => {
      const base = (Number(it.unitPriceNet) || 0) * (Number(it.quantity) || 0);
      return `· ${it.name}: ${it.coverageFrom}~${it.coverageTo} 일할 적용 (정상 ${won(base)}원 → ${won(Number(it.supplyAmountNet) || 0)}원)`;
    }).join('\n'),
    [proratedItems],
  );

  const appendProrationToRemark = () => {
    if (!prorationNote) return;
    setCustomerRemark((prev) => (prev && prev.trim() ? `${prev}\n${prorationNote}` : prorationNote));
  };

  const isDraft = settlement?.status === 'DRAFT';

  // 비고를 고쳤는데 아직 저장하지 않은 상태 — 확정 시 함께 저장된다는 것을 화면에 알린다.
  const remarkDirty = !!settlement && (
    customerRemark !== (settlement.customerRemark || '') || adminMemo !== (settlement.adminMemo || '')
  );

  // ===== 월 마감 작업 목록 =====
  // 이 화면의 본래 용도는 "매월 초에 지난달 정산서를 일괄 확정"하는 것이다. 그런데 종전에는 channelId 를
  // 손으로 입력해야 해서 '누가 남았는지'를 알 수 없었다 → 대상 채널 목록 + 진행률을 위에 붙인다.
  //
  // 대상 = **그 달에 청구할 근거가 있는 채널**. 정산서가 아직 없는 채널도 반드시 보여야 한다
  // (그게 바로 누락되는 경우다) → listCustomers 로 모집단을 잡고 listSettlement 를 좌측 조인한다.
  //
  // 🔴 종전에는 대상을 '그 달 활성 구독이 있는 채널' 하나로만 잡았다. 그래서 **구독 없이 1회성 비용
  //    (설치비·스티커 등)만 넣어둔 채널이 목록에서 통째로 사라졌다** — 청구할 금액이 있는데 담당자
  //    화면에 행 자체가 없으니 월 마감에서 조용히 빠진다(미수금 집계에도 안 잡힌다). 2026-08-31 수정.
  //    이제 근거는 세 축이고, 하나라도 있으면 행을 만든다(`basis` 참조).
  const [worklist, setWorklist] = useState([]);
  const [workLoading, setWorkLoading] = useState(false);
  /**
   * '구독이 있는 채널만 표시' — 종전 동작으로 되돌리는 스코프 토글.
   *
   * 기본값 **false**(= 1회성 비용만 있는 채널도 보인다). 이 화면의 목적이 '이 달 청구를 빠짐없이
   * 마감하는 것'이므로 **빠지는 쪽이 기본값이면 안 된다.** 구독 기준으로만 훑고 싶을 때 체크한다.
   *
   * ⚠️ 필터(플래그·상태)와 달리 **행 집합 자체를 바꾼다** → 위쪽 진행률·미수금 집계도 함께 변한다.
   *    그래서 필터 팝업이 아니라 목록 머리줄에 둔다(무엇을 세고 있는지가 눈에 보이는 자리).
   */
  const [subsOnly, setSubsOnly] = useState(false);
  /**
   * 1회성 비용 채널 조회가 실패했는지. `true` 면 **구독 기준으로만 보이는 상태**다.
   *
   * 조용히 넘기면 안 된다 — 화면은 정상처럼 보이는데 1회성 비용만 있는 채널이 여전히 빠져 있으므로,
   * 담당자가 '이 달은 그런 채널이 없구나'로 오해한다. 배포 순서(redirect 먼저)가 어긋난 신호이기도 하다.
   */
  const [feeApiDown, setFeeApiDown] = useState(false);
  // 기본값 false — 진입 시 그 달 전체가 보이는 것이 담당자의 기대에 맞다(2026-08-05 요청).
  // 종전 true 는 '확정한 건이 목록에서 사라진다'로 읽혀, 확정 결과를 되짚어 보려면 매번 체크를 풀어야 했다.
  /**
   * 정산 상태 필터 — 복수 선택(OR). 값 = 목록 행의 `status`(미생성은 'NONE').
   * 종전의 '미확정만 보기' 체크박스를 대체한다(2026-08-06) — 필터가 한 곳에 모이지 않으면
   * 필터가 늘어날 때마다 머리줄이 길어지고, 같은 축의 조건이 두 곳에 흩어진다.
   */
  const [statusFilters, setStatusFilters] = useState([]);
  /**
   * 필터 팝업 위치. `null` = 닫힘. 열 때 버튼의 화면 좌표를 담는다.
   *
   * ⚠️ **`fixed` 로 띄운다** — 목록은 `overflow` 컨테이너 안에 있고 레이아웃 상위에도 `overflow` 가 있어
   *    `absolute` 로는 잘릴 수 있다(결제 실패 툴팁에서 이미 겪은 문제).
   *    버튼 오른쪽에 맞춰 열고, 화면 왼쪽으로 넘치면 안쪽으로 클램프한다.
   */
  const [filterAnchor, setFilterAnchor] = useState(null);
  const filterOpen = filterAnchor !== null;

  const FILTER_PANEL_WIDTH = 448; // w-[28rem]

  const toggleFilterPanel = (e) => {
    if (filterOpen) { setFilterAnchor(null); return; }
    const rect = e.currentTarget.getBoundingClientRect();
    setFilterAnchor({
      top: rect.bottom + 6,
      left: Math.max(8, Math.min(rect.right - FILTER_PANEL_WIDTH, window.innerWidth - FILTER_PANEL_WIDTH - 8)),
    });
  };
  const [keyword, setKeyword] = useState('');
  /**
   * 플래그 필터 — **복수 선택(OR)**. 선택한 것 중 **하나라도** 가진 업체를 보여준다(2026-08-06).
   * 종전 단일 select 로는 "VIP 이거나 이탈위험" 같은 조회가 불가능했다.
   */
  const [flagFilters, setFlagFilters] = useState([]);
  /**
   * 수납 상태 필터 — 복수 선택(OR). 값 = `PAID`(수납 완료) / `FAILED`(결제 실패) / `UNPAID`(미수금).
   * **확정된 건에만 있는 상태**라, 하나라도 선택하면 미확정 업체는 목록에서 빠진다(미확정은 '미확정만 보기'로 본다).
   */
  const [payFilters, setPayFilters] = useState([]);
  /** 플래그 색상(flag_style). 설정 화면에서 지정한 색. 미지정 플래그는 기본색. */
  const [flagColors, setFlagColors] = useState(new Map());

  // ---- 채널 flag ----
  // 채널별 flag 를 한 번에 주는 API 가 없어(/sysadmin/flag 는 채널 1개 단위) flag 코드별 채널 id 집합을
  // 받아 뒤집는다. 코드 수만큼 호출이 나가지만 flag 코드는 운영자가 직접 만드는 소수의 라벨이고
  // 월 변경과 무관하므로 **화면 진입 시 1회만** 부른다. (백엔드 CustomerRowDto 에 flags 를 싣는 편이
  // 깔끔하지만 그러면 redirect 배포가 함께 필요해져, 이번 변경은 프론트 단독으로 끝냈다.)
  const [flagCodes, setFlagCodes] = useState([]);
  const [flagsByChannel, setFlagsByChannel] = useState(new Map());

  useEffect(() => {
    let canceled = false;
    (async () => {
      try {
        // 색·정렬순서까지 한 번에 받는다(설정 화면에서 지정한 순서대로 필터가 나열된다).
        const styles = (await listFlagStyles())?.data ?? [];
        if (canceled) return;
        const codes = styles.map((st) => st.flagCode);
        setFlagCodes(codes);
        setFlagColors(toFlagColorMap(styles));
        const sets = await Promise.all(
          codes.map((c) => listChannelsByFlag(c).then((r) => r?.data ?? []).catch(() => [])),
        );
        if (canceled) return;
        const map = new Map();
        codes.forEach((code, i) => {
          sets[i].forEach((cid) => {
            const cur = map.get(cid);
            if (cur) cur.push(code); else map.set(cid, [code]);
          });
        });
        setFlagsByChannel(map);
      } catch {
        // flag 는 부가 정보다 — 실패해도 정산 목록 자체는 그대로 쓸 수 있어야 하므로 조용히 넘어간다.
      }
    })();
    return () => { canceled = true; };
  }, []);

  // 전 채널 기본정보(로고·채널명·계정주). 작업 목록은 '활성 구독이 있는 채널'로 걸러지지만,
  // 선택된 업체 패널은 그 밖의 채널도 표시할 수 있어야 하므로 **전 채널**을 따로 들고 있는다.
  const [customerMap, setCustomerMap] = useState(new Map());
  const pickedCustomer = channelId ? customerMap.get(Number(channelId)) : null;

  const loadWorklist = useCallback(async () => {
    setWorkLoading(true);
    try {
      // 1회성 비용 채널 목록은 **실패해도 화면을 세우지 않는다.** redirect 가 아직 배포되지 않아
      // 이 엔드포인트가 없을 수 있고(qr-offline 이 먼저 뜨는 경우), 그때는 종전과 똑같이
      // '구독 기준'으로만 보이면 된다. 여기서 throw 하면 목록 전체가 오류로 비어 버린다.
      const [cust, setl, feeCh] = await Promise.all([
        listCustomers(),
        listSettlement(month),
        listOneTimeFeeChannels(month).catch(() => null),
      ]);
      setCustomerMap(new Map((cust?.data ?? []).map((c) => [c.channelId, c])));
      const byChannel = new Map((setl?.data ?? []).map((s) => [s.channelId, s]));
      const feeChannelIds = new Set((feeCh?.data ?? []).map(Number));
      setFeeApiDown(feeCh === null);

      const rows = (cust?.data ?? [])
        .map((c) => {
          const hasActiveSub = (c.subscriptions ?? []).some((s) => isBillableForMonth(s, month)); // 서버 billableIn 과 같은 규칙(ENDED 도 종료월까지는 포함)
          const s = byChannel.get(c.channelId);
          // 청구 근거 3축. 우선순위는 '구독 → 1회성 → 정산서' 순이고, 배지는 구독이 없을 때만 붙는다.
          //  SUB        = 그 달 활성 구독이 있다(종전의 유일한 기준).
          //  ONE_TIME   = 구독은 없지만 그 달 1회성 비용이 걸려 있다(이번에 되살린 경우).
          //  SETTLEMENT = 둘 다 없는데 그 달 정산서가 이미 있다(구독 종료 후 잔여 청구·수동 생성분).
          //               정산서가 존재하는데 목록에 없으면 그 금액은 미수금 집계에서도 사라진다.
          const basis = hasActiveSub ? 'SUB' : feeChannelIds.has(c.channelId) ? 'ONE_TIME' : s ? 'SETTLEMENT' : null;
          if (basis === null) return null;
          return {
            channelId: c.channelId,
            channelName: c.channelName,
            accountId: c.accountId ?? null,
            accountOwner: c.accountOwner ?? null,
            basis,
            hasActiveSub,
            settlementId: s?.id ?? null,
            status: s?.status ?? 'NONE', // NONE = 정산서 미생성
            total: s?.total ?? 0,
            billingMethod: s?.billingMethod ?? null,
            paidAt: s?.paidAt ?? null,
            paymentMethod: s?.paymentMethod ?? null,
            lastPaymentFailedAt: s?.lastPaymentFailedAt ?? null,
            lastPaymentError: s?.lastPaymentError ?? null,
            paymentFailCount: s?.paymentFailCount ?? 0,
            // 상태 배지와 함께 '언제 마지막으로 건드렸는지'를 보여주기 위한 값(2026-09-02).
            modifiedAt: s?.modifiedAt ?? null,
            confirmedAt: s?.confirmedAt ?? null,
          };
        })
        .filter(Boolean)
        .sort((a, b) => a.channelId - b.channelId);
      setWorklist(rows);
    } catch (e) {
      setMsg(`목록 조회 오류: ${errorText(e)}`);
      setMsgError(true);
    } finally {
      setWorkLoading(false);
    }
  }, [month]);

  /**
   * 스코프 적용 결과 = 진행률·미수금·목록이 모두 이걸 기준으로 센다.
   *
   * `subsOnly` 를 여기서 걸러 **재조회 없이** 토글되게 한다(행 집합은 이미 다 받아 뒀다).
   * 반대로 '미생성 초안 일괄 생성'은 서버가 월 기준으로 판단하므로 스코프와 무관하게 `worklist` 를 본다.
   */
  const scopedWork = useMemo(
    () => (subsOnly ? worklist.filter((r) => r.hasActiveSub) : worklist),
    [worklist, subsOnly],
  );

  /** 구독 없이 이 달 근거만 있는 채널 수 — 머리줄 안내에 쓴다(0 이면 아무 말도 하지 않는다). */
  const noSubCount = useMemo(() => worklist.filter((r) => !r.hasActiveSub).length, [worklist]);

  useEffect(() => { loadWorklist(); }, [loadWorklist]);


  /**
   * 월 마감 대시보드.
   * '확정'과 '수납'은 **다른 단계**다 — 확정은 금액을 동결한 것이고, 수납은 실제로 돈이 들어온 것이다.
   * 확정했지만 수납 안 된 금액이 곧 **미수금**이라 별도로 집계한다.
   */
  const progress = useMemo(() => {
    const total = scopedWork.length;
    const confirmedRows = scopedWork.filter((r) => isConfirmed(r.status));
    const paidRows = confirmedRows.filter(isPaid);
    const unpaidRows = confirmedRows.filter((r) => !isPaid(r));
    return {
      total,
      confirmed: confirmedRows.length,
      pending: total - confirmedRows.length,
      rate: total > 0 ? Math.round((confirmedRows.length / total) * 100) : 0,
      confirmedAmount: confirmedRows.reduce((a, r) => a + r.total, 0),
      pendingAmount: scopedWork.filter((r) => !isConfirmed(r.status)).reduce((a, r) => a + r.total, 0),
      paidCount: paidRows.length,
      paidAmount: paidRows.reduce((a, r) => a + r.total, 0),
      unpaidCount: unpaidRows.length,
      receivableAmount: unpaidRows.reduce((a, r) => a + r.total, 0), // 미수금
      payRate: confirmedRows.length > 0 ? Math.round((paidRows.length / confirmedRows.length) * 100) : 0,
    };
  }, [scopedWork]);

  const toggleIn = (arr, setArr, value) =>
    setArr(arr.includes(value) ? arr.filter((v) => v !== value) : [...arr, value]);

  /** 버튼에 표시할 걸린 조건 수(검색어는 별도 입력칸이라 세지 않는다). */
  const activeFilterCount = flagFilters.length + statusFilters.length + payFilters.length;

  const clearFilters = () => { setFlagFilters([]); setStatusFilters([]); setPayFilters([]); };

  /**
   * 화면에 실제로 보이는 행. '미확정만' + 검색어 + 플래그 세 조건의 교집합이다.
   * 검색 대상은 '전체 고객' 화면과 같은 조합(채널 id / 채널명 / 계정 id / 계정주).
   */
  const visibleWork = useMemo(() => {
    const kw = keyword.trim().toLowerCase();
    return scopedWork.filter((r) => {
      if (statusFilters.length > 0 && !statusFilters.includes(r.status)) return false;
      if (flagFilters.length > 0) {
        const mine = flagsByChannel.get(r.channelId) ?? [];
        if (!flagFilters.some((f) => mine.includes(f))) return false;
      }
      if (payFilters.length > 0 && !payFilters.includes(payStateOf(r))) return false;
      if (kw) {
        const hay = `${r.channelId} ${r.channelName ?? ''} ${r.accountId ?? ''} ${r.accountOwner ?? ''}`.toLowerCase();
        if (!hay.includes(kw)) return false;
      }
      return true;
    });
  }, [scopedWork, statusFilters, keyword, flagFilters, payFilters, flagsByChannel]);

  // 보이는 목록의 청구 총액 합계. 필터를 좁히면 그 범위의 합계가 된다(= 지금 보고 있는 것의 합).
  const visibleTotal = useMemo(
    () => visibleWork.reduce((a, r) => a + (r.status === 'NONE' ? 0 : r.total), 0),
    [visibleWork],
  );

  // ---- 수납 처리 ----

  /** 작업 후 화면 동기화(목록 + 열려 있는 정산서). */
  const reloadAfterWork = async () => {
    await loadWorklist();
    if (channelId) {
      const resp = await getSettlement(channelId, month);
      apply(resp?.data ?? null);
    }
  };

  const runWork = async (fn, okMsg) => {
    setLoading(true);
    setMsg('');
    setMsgError(false);
    try {
      await fn();
      await reloadAfterWork();
      if (okMsg) setMsg(okMsg);
    } catch (e) {
      setMsg(`오류: ${errorText(e)}`);
      setMsgError(true);
    } finally {
      setLoading(false);
    }
  };

  /**
   * 카드 결제 실행 — legacy 빌링('수동 결제')과 동일하게 **결제금액 확인 팝업**을 거친다.
   *
   * 종전에는 `window.confirm` 이라 총액 한 줄만 보였다. 결제는 실제 카드 승인이 올라가고 되돌리기가
   * 번거로우므로, 무엇에 대해 얼마를 청구하는지(항목 내역·공급가·VAT) 보여준 뒤 사람이 마지막으로 누르게 한다.
   *
   * ⚠️ legacy 와 달리 **금액은 수정할 수 없다.** 확정된 정산서 `total` 그대로 청구해야 원장(settlement)과
   *    실제 카드 승인액이 어긋나지 않는다. 금액을 바꿔야 하면 재오픈 → 항목 수정 → 재확정이 정규 경로다.
   */
  const [payTarget, setPayTarget] = useState(null); // { row, settlement, loading, error }
  /** 결제 예정 안내 문자 패널(2026-10-02). 결제 실행 전에 고객에게 금액·결제일·고지서 링크를 보낸다. */
  const [noticeOpen, setNoticeOpen] = useState(false);
  const [paying, setPaying] = useState(false);

  const openPayModal = async (row) => {
    setPayTarget({ row, settlement: null, loading: true, error: '' });
    try {
      const resp = await getSettlement(row.channelId, month);
      setPayTarget({ row, settlement: resp?.data ?? null, loading: false, error: '' });
    } catch {
      // 내역을 못 불러와도 총액 확인만으로 결제할 수 있게 둔다(목록의 total 은 이미 확정 동결값).
      setPayTarget({ row, settlement: null, loading: false, error: '' });
    }
  };

  /**
   * 카드 결제 실행.
   *
   * **실패해도 모달을 닫지 않고 그 자리에 사유를 띄운다**(2026-08-05). 종전에는
   *  ① 모달을 먼저 닫고 ② 서버가 실패를 500 으로 내려 ③ `fetch.js` 가 `/notfound` 로 보내
   * **화면 자체가 튕겼다.** 실패 사유(`잔액부족으로 결제에 실패했습니다.` / `A7:도난/분실위변조카드` 등)는
   * 슬랙 알림에만 남아서, 담당자는 무엇이 왜 실패했는지 화면에서 알 수 없었다.
   *
   * 실패 시 서버는 아무것도 바꾸지 않으므로(수납 표시 없음) 사유를 확인하고 **그대로 재시도**할 수 있다.
   */
  const doPayCard = async () => {
    const row = payTarget?.row;
    if (!row || paying) return;
    const name = `#${row.channelId} ${row.channelName}`;

    setPaying(true);
    setPayTarget((prev) => (prev ? { ...prev, error: '' } : prev));
    setMsg('');
    try {
      await paySettlementByCard(row.settlementId);
      setPayTarget(null);
      await reloadAfterWork();
      setMsg(`${name} 카드 결제 완료 — 수납 마감`);
      setMsgError(false);
    } catch (e) {
      const reason = errorText(e);
      // 모달 안(작업한 자리) + 화면 상단 배너 두 곳에 남긴다. 모달을 닫아도 사유가 사라지지 않도록.
      setPayTarget((prev) => (prev ? { ...prev, error: reason } : prev));
      setMsg(`${name} 결제 실패 — ${reason}`);
      setMsgError(true);
      // 실패 사유가 '이미 수납됨'처럼 상태 불일치인 경우가 있어 목록을 새로고침해 현재 상태를 맞춘다.
      await reloadAfterWork().catch(() => {});
    } finally {
      setPaying(false);
    }
  };

  const doMarkDeposit = (row) => {
    const name = `#${row.channelId} ${row.channelName}`;
    const memo = window.prompt(`${name}\n\n${won(row.total)}원 입금을 확인했습니까?\n입금자명·이체일 등 메모(선택)`, '');
    if (memo === null) return; // 취소
    runWork(() => markSettlementDeposit(row.settlementId, memo || null), `${name} 입금 확인 — 수납 마감`);
  };

  const doUnmarkPaid = (row) => {
    if (!window.confirm(
      `#${row.channelId} ${row.channelName}\n\n수납 완료 표시를 취소합니다.\n⚠️ 카드로 결제된 건이라면 승인 취소는 별도로 진행해야 합니다.\n\n계속할까요?`
    )) return;
    runWork(() => unmarkSettlementPaid(row.settlementId), '수납 취소 완료');
  };

  const doGenerateMissing = () => {
    // 🔴 **`scopedWork` 가 아니라 `worklist`(전체) 를 센다.** 서버는 대상월 기준으로 판단하므로
    //    '구독이 있는 채널만 표시'를 체크해 둔 상태에서도 1회성 비용 채널의 초안까지 만든다.
    //    화면에 보이는 수만 세면 "3개라더니 5개가 생겼다"가 되어 담당자가 결과를 못 믿는다.
    const missingRows = worklist.filter((r) => r.status === 'NONE');
    const missing = missingRows.length;
    const missingNoSub = missingRows.filter((r) => !r.hasActiveSub).length;
    if (missing === 0) { setMsg('초안이 없는 채널이 없습니다.'); return; }
    const noSubNote = missingNoSub > 0
      ? `\n(구독 없이 1회성 비용만 있는 채널 ${missingNoSub}개 포함)`
      : '';
    if (!window.confirm(`정산서가 없는 ${missing}개 채널의 초안을 생성합니다.${noSubNote}\n이미 있는 정산서는 건드리지 않습니다.\n\n진행할까요?`)) return;
    runWork(async () => {
      const res = await generateMissingSettlements(month);
      const d = res?.data ?? {};
      if ((d.failedChannelIds ?? []).length > 0) {
        setMsg(`초안 ${d.created}/${d.attempted}건 생성. 실패 채널: ${d.failedChannelIds.join(', ')}`);
      } else {
        setMsg(`초안 ${d.created}건 생성 완료`);
      }
    });
  };

  /**
   * **전체 초안 재생성**(2026-09-02). '미생성 초안 일괄 생성'과 달리 **이미 있는 초안도 다시 만든다.**
   *
   * 왜 필요한가: 마감 도중 정가표·구독·1회성 비용을 고치면 이미 만들어 둔 초안은 옛 금액으로 남는다.
   * 종전에는 채널을 하나씩 눌러야 했고, 어디를 다시 눌렀는지 사람이 기억해야 했다.
   *
   * 🔴 **확정분은 재생성하지 않는다**(서버가 제외). 취소분도 제외한다.
   * ⚠️ **DRAFT 에 손으로 넣은 항목·비고는 사라진다** — 그래서 확인을 2단으로 받는다.
   *    1단 = 무엇이 몇 건 바뀌는지, 2단 = 수기 편집이 날아간다는 경고.
   *    `doGenerateMissing` 과 같은 이유로 **`scopedWork` 가 아니라 `worklist`(전체) 를 센다** —
   *    서버는 대상월 기준으로 판단하므로 보이는 수만 세면 결과를 못 믿게 된다.
   */
  const doRegenerateAll = () => {
    const draftCnt = worklist.filter((r) => r.status === 'DRAFT').length;
    const noneCnt = worklist.filter((r) => r.status === 'NONE').length;
    const confirmedCnt = worklist.filter((r) => isConfirmed(r.status)).length;
    const canceledCnt = worklist.filter((r) => r.status === 'CANCELED').length;

    if (draftCnt + noneCnt === 0) {
      setMsg(confirmedCnt > 0
        ? `${month} 은 재생성할 초안이 없습니다(확정 ${confirmedCnt}건은 재생성 대상이 아닙니다).`
        : `${month} 은 재생성할 대상이 없습니다.`);
      return;
    }

    const lines = [
      `${month} 정산 초안을 전부 다시 만듭니다.`,
      '',
      `· 기존 초안 재생성: ${draftCnt}건`,
      `· 정산서 없어 새로 생성: ${noneCnt}건`,
    ];
    if (confirmedCnt > 0) lines.push(`· 확정분 ${confirmedCnt}건 → 그대로 둡니다(재생성 안 함)`);
    if (canceledCnt > 0) lines.push(`· 취소분 ${canceledCnt}건 → 그대로 둡니다`);
    lines.push('', '진행할까요?');
    if (!window.confirm(lines.join('\n'))) return;

    // 2단 경고 — 되돌릴 수 없는 부분만 따로 묻는다. 초안이 하나도 없으면(전부 신규 생성) 생략한다.
    if (draftCnt > 0 && !window.confirm(
      `⚠️ 되돌릴 수 없습니다.\n\n`
      + `기존 초안 ${draftCnt}건은 항목을 전부 지우고 현재 데이터로 다시 계산합니다.\n`
      + `→ 그 초안에 **손으로 넣거나 고친 항목**이 있으면 사라집니다.\n\n`
      + `확정된 정산서는 영향받지 않습니다.\n\n계속할까요?`
    )) return;

    runWork(async () => {
      const res = await regenerateAllSettlements(month);
      const d = res?.data ?? {};
      const parts = [`재생성 ${d.regenerated ?? 0}건`, `신규 ${d.created ?? 0}건`];
      if (d.skippedConfirmed) parts.push(`확정분 ${d.skippedConfirmed}건 제외`);
      if (d.skippedCanceled) parts.push(`취소분 ${d.skippedCanceled}건 제외`);
      const failed = d.failedChannelIds ?? [];
      if (failed.length > 0) {
        setMsg(`${parts.join(' · ')} — 실패 채널: ${failed.join(', ')}`);
        setMsgError(true);
      } else {
        setMsg(`${parts.join(' · ')} 완료`);
      }
    });
  };

  // 목록에서 채널을 고르면 아래 편집 영역으로 그 채널을 불러온다(없으면 초안 생성까지).
  const pickChannel = async (cid) => {
    setChannelId(String(cid));
    setLoading(true);
    setMsg('');
    setMsgError(false);
    try {
      let resp = await getSettlement(cid, month);
      if (!resp?.data) {
        await generateSettlement(cid, month);
        resp = await getSettlement(cid, month);
        setMsg('정산서가 없어 초안을 생성했습니다.');
      }
      apply(resp?.data ?? null);
    } catch (e) {
      setMsg(`오류: ${errorText(e)}`);
      setMsgError(true);
    } finally {
      setLoading(false);
    }
  };

  // 확정/재오픈 후 진행률이 즉시 반영되도록 목록도 함께 갱신한다.
  const doConfirmAndRefresh = () => doConfirm().then(loadWorklist);
  const doReopenAndRefresh = () => doReopen().then(loadWorklist);

  return (
    <div className="p-6 space-y-4">
      {noticeOpen && <BillingNoticePanel month={month} onClose={() => setNoticeOpen(false)} />}
      {/* ===== ① 대상 월 선택 — 이 화면의 모든 것이 이 값 기준이다 ===== */}
      <div className="flex items-center flex-wrap gap-3">
        <h2 className="text-xl font-bold">정산 관리</h2>
        <select
          className="border rounded px-3 py-1.5 text-base font-semibold"
          value={month}
          onChange={(e) => setMonth(e.target.value)}
        >
          {months.map((m) => <option key={m} value={m}>{m}</option>)}
        </select>
        <span className="text-xs text-gray-500">매월 초에 지난달 정산서를 확정하고 수납까지 마감하는 화면입니다.</span>
      </div>

      {/* ===== ② 대시보드 — 확정 진행 + 수납(미수금) 현황 ===== */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <div className="border rounded-lg p-4 bg-white">
          <div className="text-xs text-gray-500">정산 확정</div>
          <div className="text-2xl font-bold mt-1">
            {progress.confirmed}<span className="text-base font-normal text-gray-400"> / {progress.total} 개사</span>
          </div>
          <div className="h-2 w-full bg-gray-200 rounded overflow-hidden mt-2">
            <div className="h-full bg-emerald-500" style={{ width: `${progress.rate}%` }} />
          </div>
          <div className="text-xs text-gray-400 mt-1">{progress.rate}% 완료</div>
        </div>
        <div className="border rounded-lg p-4 bg-white">
          <div className="text-xs text-gray-500">확정 금액</div>
          <div className="text-2xl font-bold mt-1 text-emerald-700">{won(progress.confirmedAmount)}<span className="text-sm font-normal text-gray-400"> 원</span></div>
          <div className="text-xs text-gray-400 mt-1">미확정 {won(progress.pendingAmount)}원</div>
        </div>
        <div className="border rounded-lg p-4 bg-white">
          <div className="text-xs text-gray-500">수납 완료</div>
          <div className="text-2xl font-bold mt-1 text-blue-700">{won(progress.paidAmount)}<span className="text-sm font-normal text-gray-400"> 원</span></div>
          <div className="text-xs text-gray-400 mt-1">{progress.paidCount} / {progress.confirmed} 개사 ({progress.payRate}%)</div>
        </div>
        <div className={`border rounded-lg p-4 ${progress.receivableAmount > 0 ? 'bg-amber-50 border-amber-300' : 'bg-white'}`}>
          <div className="text-xs text-gray-500">미수금 (확정·미수납)</div>
          <div className={`text-2xl font-bold mt-1 ${progress.receivableAmount > 0 ? 'text-amber-700' : 'text-gray-400'}`}>
            {won(progress.receivableAmount)}<span className="text-sm font-normal text-gray-400"> 원</span>
          </div>
          <div className="text-xs text-gray-400 mt-1">{progress.unpaidCount} 개사</div>
        </div>
      </div>

      {/* ===== ③ 작업 목록 ===== */}
      <div className="border rounded">
        <div className="flex items-center flex-wrap gap-3 bg-gray-50 border-b px-4 py-3">
          <span className="font-semibold">{month} 대상 업체</span>
          {/* 스코프 토글. 필터 팝업이 아니라 여기 두는 이유 = 행 집합(=위쪽 진행률·미수금의 분모)이
              바뀌므로 무엇을 세고 있는지가 목록 머리줄에서 바로 보여야 한다. */}
          <label
            className="flex items-center gap-1.5 text-xs text-gray-600 cursor-pointer whitespace-nowrap"
            title="체크하면 그 달 활성 구독이 있는 채널만 봅니다(종전 동작). 해제하면 구독이 없어도 그 달 1회성 비용이나 정산서가 있는 채널이 함께 보입니다."
          >
            <input
              type="checkbox"
              checked={subsOnly}
              onChange={(e) => setSubsOnly(e.target.checked)}
            />
            구독이 있는 채널만 표시
          </label>
          {!subsOnly && noSubCount > 0 && (
            <span className="text-xs text-gray-500 whitespace-nowrap">
              구독 없이 청구 근거만 있는 채널 {noSubCount}개 포함
            </span>
          )}
          {feeApiDown && (
            /* 1회성 비용 채널 조회 실패 = 구독 기준으로만 보이는 상태. 조용히 넘기면
               "1회성 비용만 있는 채널이 없다"로 오해한다(배포 순서가 어긋난 신호이기도 하다). */
            <span className="text-xs text-red-700 bg-red-50 border border-red-200 rounded px-1.5 py-0.5 whitespace-nowrap">
              1회성 비용 채널 조회 실패 — 지금은 구독 기준으로만 보입니다
            </span>
          )}
          <button
            className="px-2 py-1 text-sm border rounded bg-white"
            onClick={doGenerateMissing}
            disabled={loading || workLoading}
            title="정산서가 없는 채널의 초안을 한 번에 만듭니다. 이미 있는 정산서는 건드리지 않습니다."
          >
            미생성 초안 일괄 생성
          </button>
          {/* 파괴적인 쪽(기존 초안을 덮어쓴다)이라 색으로 구분한다 — 옆 버튼과 같은 흰 버튼이면
              마감 중에 잘못 누른다. 확인은 doRegenerateAll 이 2단으로 받는다. */}
          <button
            className="px-2 py-1 text-sm border rounded bg-amber-50 border-amber-400 text-amber-800"
            onClick={doRegenerateAll}
            disabled={loading || workLoading}
            title="이미 있는 초안까지 전부 다시 만듭니다(정가표·구독을 고친 뒤 금액을 맞출 때). 확정된 정산서는 건드리지 않습니다. ⚠️ 초안에 손으로 넣은 항목은 사라집니다."
          >
            전체 초안 재생성
          </button>
          {/* 결제 실행 전에 보낸다 — 예고 없는 결제가 '사기 결제' 오해·카드사 민원으로 이어졌다(2026-10-02). */}
          <button
            className="px-2 py-1 text-sm border rounded bg-blue-50 border-blue-400 text-blue-800"
            onClick={() => setNoticeOpen(true)}
            disabled={loading || workLoading}
            title="확정된 정산서의 고객에게 결제 예정 금액·결제일과 모바일 고지서 링크를 문자(LMS)로 보냅니다. 결제 실행 전에 보내세요."
          >
            결제 안내 문자
          </button>
          <input
            className="border rounded px-2 py-1 text-sm ml-auto w-56"
            placeholder="검색(채널명/id/계정)"
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
          />
          {/* ===== 필터 =====
              버튼 + 팝업 구조다(2026-08-06). 종전에는 체크박스를 헤더 아래 한 줄로 늘어놓았는데,
              **필터가 늘어날수록 그 줄이 무한히 길어진다.** 팝업이면 항목을 추가해도 화면 높이가 그대로다.
              열려 있는 동안 뒤에 투명 배경막을 깔아 **바깥을 누르면 닫힌다**(document 리스너 없이 처리). */}
          <div className="relative">
            <button
              className={`px-2 py-1 text-sm border rounded ${
                activeFilterCount > 0 ? 'bg-indigo-50 border-indigo-400 text-indigo-700 font-semibold' : 'bg-white'
              }`}
              onClick={toggleFilterPanel}
              title="플래그 · 정산 상태 · 수납 상태로 걸러 봅니다."
            >
              필터{activeFilterCount > 0 ? ` (${activeFilterCount})` : ''}
            </button>

            {filterOpen && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setFilterAnchor(null)} />
                <div className="fixed z-50 w-[28rem] max-h-[26rem] overflow-y-auto
                                bg-white border rounded-lg shadow-2xl p-4 space-y-4 text-sm"
                     style={{ top: filterAnchor.top, left: filterAnchor.left }}>
                  <div className="flex items-center justify-between">
                    <span className="font-bold">필터</span>
                    <div className="flex items-center gap-2">
                      <button
                        className="text-xs px-2 py-0.5 border rounded bg-white"
                        onClick={() => setStatusFilters(PENDING_STATUSES)}
                        title="미생성 + 초안만 봅니다(종전 '미확정만 보기')."
                      >
                        미확정만
                      </button>
                      <button className="text-xs underline text-gray-400" onClick={clearFilters}>모두 해제</button>
                      <button className="text-xs underline text-gray-400" onClick={() => setFilterAnchor(null)}>닫기</button>
                    </div>
                  </div>

                  {/* 플래그 */}
                  <div>
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="text-xs font-semibold text-gray-500">플래그</span>
                      {flagFilters.length > 0 && (
                        <button className="text-xs underline text-gray-400" onClick={() => setFlagFilters([])}>해제</button>
                      )}
                    </div>
                    {flagCodes.length === 0 ? (
                      <div className="text-xs text-gray-400">등록된 플래그가 없습니다</div>
                    ) : (
                      <div className="flex flex-wrap gap-x-3 gap-y-1.5">
                        {flagCodes.map((c) => (
                          <label key={c} className="flex items-center gap-1 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={flagFilters.includes(c)}
                              onChange={() => toggleIn(flagFilters, setFlagFilters, c)}
                            />
                            <span className="px-1.5 py-0.5 rounded text-xs whitespace-nowrap"
                                  style={flagBadgeStyle(flagColors.get(c))}>
                              {c}
                            </span>
                          </label>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* 정산 상태 */}
                  <div>
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="text-xs font-semibold text-gray-500">정산 상태</span>
                      {statusFilters.length > 0 && (
                        <button className="text-xs underline text-gray-400" onClick={() => setStatusFilters([])}>해제</button>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-x-3 gap-y-1.5">
                      {STATUS_FILTERS.map((f) => (
                        <label key={f.value} className="flex items-center gap-1 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={statusFilters.includes(f.value)}
                            onChange={() => toggleIn(statusFilters, setStatusFilters, f.value)}
                          />
                          <span className={`px-1.5 py-0.5 rounded text-xs whitespace-nowrap ${f.badge}`}>{f.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* 수납 상태 */}
                  <div>
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="text-xs font-semibold text-gray-500">
                        수납 상태 <span className="font-normal text-gray-400">(확정된 건만 대상)</span>
                      </span>
                      {payFilters.length > 0 && (
                        <button className="text-xs underline text-gray-400" onClick={() => setPayFilters([])}>해제</button>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-x-3 gap-y-1.5">
                      {PAY_FILTERS.map((f) => (
                        <label key={f.value} className="flex items-center gap-1 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={payFilters.includes(f.value)}
                            onChange={() => toggleIn(payFilters, setPayFilters, f.value)}
                          />
                          <span className={`px-1.5 py-0.5 rounded text-xs whitespace-nowrap ${f.badge}`}>{f.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  <div className="border-t pt-2 text-xs text-gray-400">
                    같은 항목 안에서는 <b>선택한 것 중 하나라도</b> 해당하면 보이고, 다른 항목끼리는 <b>모두</b> 만족해야 보입니다.
                    아무것도 체크하지 않은 항목은 걸러내지 않습니다.
                  </div>
                </div>
              </>
            )}
          </div>

          <button className="px-2 py-1 text-sm border rounded bg-white" onClick={loadWorklist} disabled={workLoading}>
            새로고침
          </button>
        </div>
        <div className="max-h-[30rem] overflow-y-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-white sticky top-0 border-b text-xs">
              <tr className="divide-x">
                {/* 폭 배분(2026-09-02 2차): '정산 상태 · 최종수정' 을 한 줄로 담으려면 폭이 필요하고,
                    '채널' 은 플래그가 다음 줄로 넘어가도 읽는 데 지장이 없다 → 채널을 줄이고 상태를 늘렸다.
                    `w-*` 는 table-layout:auto 에서 '희망 폭' 이므로 내용이 넘치면 브라우저가 늘려 준다
                    (= 긴 채널명이 잘리지 않는다). */}
                <th className="px-3 py-1 text-left w-[18rem]">채널</th>
                <th className="px-3 py-1 w-[13rem] whitespace-nowrap" title="정산 상태 + 최종 수정일시(KST) — 그 정산서를 마지막으로 건드린 시각입니다.">정산 상태<span className="font-normal text-gray-400"> · 최종수정</span></th>
                <th className="px-3 py-1 text-right">청구 총액</th>
                <th className="px-3 py-1">청구 방식</th>
                <th className="px-3 py-1">수납 상태</th>
                <th className="px-3 py-1">작업</th>
              </tr>
            </thead>
            <tbody>
              {visibleWork.length === 0 && (
                <tr><td className="px-3 py-4 text-center text-gray-400" colSpan={6}>
                  {workLoading
                    ? '불러오는 중…'
                    : activeFilterCount > 0
                      ? '조건에 맞는 업체가 없습니다. 필터를 확인해 주세요.'
                      : subsOnly && worklist.length > 0
                        /* 행은 있는데 스코프 때문에 다 가려진 경우 — 필터를 아무리 봐도 원인을 못 찾는다. */
                        ? `구독이 있는 채널이 없습니다. '구독이 있는 채널만 표시'를 해제하면 ${worklist.length}개 업체가 보입니다.`
                        : '대상 채널이 없습니다.'}
                </td></tr>
              )}
              {visibleWork.map((r) => (
                <tr key={r.channelId} className={`border-b divide-x ${String(channelId) === String(r.channelId) ? 'bg-indigo-50' : ''}`}>
                  <td className="px-3 py-1">
                    <span className="whitespace-nowrap">#{r.channelId} {r.channelName}</span>
                    {BASIS_BADGE[r.basis] && (
                      <span className={`ml-1.5 px-1.5 py-0.5 rounded text-xs whitespace-nowrap ${BASIS_BADGE[r.basis].className}`}
                            title={BASIS_BADGE[r.basis].title}>
                        {BASIS_BADGE[r.basis].label}
                      </span>
                    )}
                    {/* '고객사' 링크는 채널명 바로 옆이 자연스럽다 — 종전에는 '작업' 칸 맨 끝이라
                        결제·수납 버튼 사이에 섞여 눌러야 할 것을 찾기 어려웠다. */}
                    {onOpenDetail && (
                      <button className="ml-2 text-xs underline text-gray-500 whitespace-nowrap"
                              onClick={() => onOpenDetail(r.channelId)}>고객사</button>
                    )}
                    {(flagsByChannel.get(r.channelId) ?? []).map((f) => (
                      <span key={f} className="ml-1 px-1.5 py-0.5 rounded text-xs whitespace-nowrap"
                            style={flagBadgeStyle(flagColors.get(f))}>{f}</span>
                    ))}
                  </td>
                  {/* 🔑 상태 배지만으로는 '방금 만든 초안' 과 '3주 전에 만들어 두고 잊은 초안' 을
                      구분할 수 없다. 마감 중에는 그 차이가 판단을 바꾼다(오래된 초안 = 그 뒤에 바뀐
                      구독·정가가 반영되지 않았을 수 있다 → 재생성 대상).
                      `settlement.modified_at` 은 `on update CURRENT_TIMESTAMP` 라 초안 재생성·항목
                      편집·비고·확정·수납 표시 등 **모든 변경**에 갱신된다. 서버가 KST 로 굳혀 내려준다.
                      ⚠️ 2026-09-02 2차: 처음에는 배지 **아래** 2줄로 넣었는데 **모든 행의 높이가 늘어났다.**
                         정산 목록은 수십 행을 훑는 화면이라 행 높이가 곧 한 화면에 보이는 업체 수다
                         ⇒ 배지와 같은 줄(수평)로 옮기고, 대신 이 칸의 폭을 늘렸다. */}
                  <td className="px-3 py-1">
                    <div className="flex items-center justify-center gap-1.5 whitespace-nowrap">
                      {r.status === 'NONE'
                        ? <span className="px-1.5 py-0.5 rounded text-xs bg-red-100 text-red-700">{statusLabel('NONE')}</span>
                        : <span className={`px-1.5 py-0.5 rounded text-xs ${STATUS_BADGE[r.status] ?? 'bg-gray-100'}`}>{statusLabel(r.status)}</span>}
                      {r.status !== 'NONE' && r.modifiedAt && (
                        <span
                          className="text-[11px] text-gray-400"
                          title={
                            `최종 수정 ${r.modifiedAt} (KST)`
                            + (r.confirmedAt ? `\n확정 ${r.confirmedAt}` : '\n확정 전')
                          }
                        >
                          {r.modifiedAt}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-3 py-1 text-right">{r.status === 'NONE' ? '—' : `${won(r.total)}원`}</td>
                  <td className="px-3 py-1 text-center text-xs text-gray-600">
                    {r.status === 'NONE' ? '—' : (r.billingMethod === 'TAX_INVOICE' ? '세금계산서' : '카드')}
                  </td>
                  <td className="px-3 py-1 text-center">
                    {isPaid(r) ? (
                      <span className="px-1.5 py-0.5 rounded text-xs bg-blue-100 text-blue-800"
                            title={`${String(r.paidAt).slice(0, 10)} · ${r.paymentMethod === 'CARD' ? '카드' : '입금'}`}>
                        수납 완료
                      </span>
                    ) : isConfirmed(r.status) && r.lastPaymentFailedAt ? (
                      /* 결제 실패 = 확정됐고 미수금인데 마지막 카드 결제가 실패한 상태.
                         '결제 실행' 버튼은 아래에서 그대로 열려 있어 재시도할 수 있다(실패해도 서버는 아무것도 안 바꾼다). */
                      <span className="px-1.5 py-0.5 rounded text-xs bg-red-100 text-red-700 font-semibold cursor-help underline decoration-dotted"
                            onMouseEnter={(e) => showFailTip(e, r)}
                            onMouseLeave={() => setFailTip(null)}>
                        결제 실패{r.paymentFailCount > 1 ? ` (${r.paymentFailCount}회)` : ''}
                      </span>
                    ) : isConfirmed(r.status) ? (
                      <span className="px-1.5 py-0.5 rounded text-xs bg-amber-100 text-amber-700">미수금</span>
                    ) : (
                      <span className="text-gray-300 text-xs">—</span>
                    )}
                  </td>
                  <td className="px-3 py-1 text-center whitespace-nowrap">
                    <button className="text-xs underline mr-2" onClick={() => pickChannel(r.channelId)}>
                      {r.status === 'NONE' ? '초안 생성' : '열기'}
                    </button>

                    {/* 확정된 건에만 수납 버튼이 열린다. 청구 방식에 따라 카드 결제 / 입금 확인으로 갈린다. */}
                    {isConfirmed(r.status) && !isPaid(r) && (
                      r.billingMethod === 'TAX_INVOICE' ? (
                        <button className="text-xs px-2 py-0.5 mr-2 border rounded bg-white border-blue-300 text-blue-700"
                                onClick={() => doMarkDeposit(r)} disabled={loading}>입금 확인</button>
                      ) : (
                        <button className="text-xs px-2 py-0.5 mr-2 border rounded bg-emerald-600 text-white"
                                onClick={() => openPayModal(r)} disabled={loading}>결제 실행</button>
                      )
                    )}
                    {isPaid(r) && (
                      <button className="text-xs underline text-gray-400" onClick={() => doUnmarkPaid(r)}>수납 취소</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* 지금 보이는 목록의 청구 총액. 스크롤 영역 밖에 둬서 목록을 내려도 계속 보이게 한다.
            상단 대시보드의 '확정 금액'과 다른 값이다 — 이쪽은 **필터가 걸린 현재 화면**의 합이다. */}
        <div className="flex items-center gap-3 border-t bg-gray-50 px-4 py-2 text-sm">
          <span className="text-gray-500">표시 중 {visibleWork.length}개사</span>
          {(keyword.trim() || activeFilterCount > 0) && (
            <span className="text-xs text-gray-400">(필터 적용됨 · 전체 {worklist.length}개사)</span>
          )}
          <span className="ml-auto text-gray-600">청구 총액 합계</span>
          <b className="text-base text-gray-900">{won(visibleTotal)}원</b>
        </div>
      </div>

      {/* ===== ④ 선택된 업체 ─ 아래 편집 영역이 어느 업체 것인지 =====
          종전에는 여기서 채널 ID 를 손으로 입력해 조회했는데, 위 목록에 검색이 생겨 쓸모가 없어졌다
          (오타로 엉뚱한 채널을 열 위험만 남는다) → 입력칸·조회 버튼을 없애고 업체 정보를 크게 보여준다. */}
      {channelId && (
        <div className="flex flex-wrap items-center gap-4 bg-gray-50 border rounded-lg p-4">
          {pickedCustomer ? (
            <>
              {pickedCustomer.logoUrl
                ? <img src={pickedCustomer.logoUrl} alt="" className="w-10 h-10 rounded object-contain bg-white border"
                       onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                : <span className="w-10 h-10 rounded bg-gray-200 inline-flex items-center justify-center text-[10px] text-gray-400">로고</span>}
              <div>
                <div className="flex items-center gap-2">
                  <span className="font-mono text-sm text-gray-500">#{channelId}</span>
                  <span className="text-lg font-bold text-gray-900">{pickedCustomer.channelName}</span>
                  <span className={`px-1.5 py-0.5 rounded text-xs ${pickedCustomer.serviceStatus === 'ACTIVE' ? 'bg-blue-100 text-blue-800' : 'bg-gray-200 text-gray-500'}`}>
                    {pickedCustomer.serviceStatus}
                  </span>
                  {(flagsByChannel.get(Number(channelId)) ?? []).map((f) => (
                    <span key={f} className="px-1.5 py-0.5 rounded text-xs"
                          style={flagBadgeStyle(flagColors.get(f))}>{f}</span>
                  ))}
                </div>
                <div className="text-sm text-gray-500 mt-0.5">
                  계정주 {pickedCustomer.accountOwner ?? '—'}
                  {pickedCustomer.accountId ? ` (${pickedCustomer.accountId})` : ''}
                  {' · 청구월 '}<b className="text-gray-800">{month}</b>
                  {onOpenDetail && (
                    <button className="ml-2 text-xs underline text-gray-500"
                            onClick={() => onOpenDetail(Number(channelId))}>고객사 상세</button>
                  )}
                </div>
              </div>
            </>
          ) : (
            <div className="text-sm text-red-600">채널 #{channelId} — 목록에 없는 채널입니다(ID 확인 필요)</div>
          )}
          <button className="ml-auto bg-indigo-600 text-white px-3 py-2 rounded disabled:opacity-50"
                  onClick={doGenerate} disabled={loading}>초안 생성/재생성</button>
        </div>
      )}

      {msg && (
        <div className={`text-sm px-3 py-2 rounded whitespace-pre-line ${
          msgError ? 'bg-red-50 border border-red-300 text-red-700 font-semibold' : 'bg-gray-100'
        }`}>
          {msgError ? `⚠️ ${msg}` : msg}
        </div>
      )}

      {settlement && (
        <div className="space-y-4">
          <div className="flex flex-wrap items-center gap-4 border rounded-lg p-4">
            <span className={`px-2 py-0.5 rounded text-xs font-semibold ${STATUS_BADGE[settlement.status] || ''}`}>
              {statusLabel(settlement.status)}
            </span>
            <span className="text-sm">청구방식: <b>{settlement.billingMethod === 'TAX_INVOICE' ? '세금계산서' : '카드 결제'}</b></span>
            <span className="text-sm">공급가: <b>{won(settlement.supplyTotal)}</b></span>
            <span className="text-sm">VAT: <b>{won(settlement.vat)}</b></span>
            <span className="text-sm">합계: <b>{won(settlement.total)}</b></span>
            {settlement.confirmedAt && (
              <span className="text-xs text-gray-500">확정: {settlement.confirmedAt} / {settlement.confirmedBy}</span>
            )}
            {/* 체크박스는 **채널 설정(isTaxInvoice)** 에 묶는다.
                billingMethod 는 정산서 생성 시점 스냅샷이라, 여기에 묶으면 토글해도 체크가 안 된다. */}
            <label className="ml-auto flex items-center gap-1 text-sm">
              <input type="checkbox" checked={!!settlement.isTaxInvoice}
                     onChange={(e) => doToggleTax(e.target.checked)} />
              세금계산서 발행 업체
            </label>
            <span className="text-xs text-gray-500">
              이 정산서: {settlement.billingMethod === 'TAX_INVOICE' ? '세금계산서' : '카드 결제'}
            </span>
          </div>

          {/* 메시지 발송 수량 합계 — 대시보드 '메시지 발송' 과 대조용(2026-10-01). 금액이 아니라 건수만 모은다. */}
          <div className="border rounded-lg px-4 py-2 bg-blue-50 text-sm">
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
              <span className="font-semibold">메시지 발송 수량 합계 {messageQty.total.toLocaleString('ko-KR')}건</span>
              {messageQty.parts.length > 0 && (
                <span className="text-gray-600">
                  = {messageQty.parts.map(([k, v]) => `${k} ${v.toLocaleString('ko-KR')}`).join(' · ')}
                </span>
              )}
              {messageQty.acquisition > 0 && (
                <span className="text-gray-600">| 고객 획득(가입) {messageQty.acquisition.toLocaleString('ko-KR')}명</span>
              )}
            </div>
            <div className="text-xs text-gray-500 mt-1">
              발송 성공(result=2)·KST 월 경계 기준으로, 대시보드와 같다.
              {messageQty.acquisition > 0 && (
                <> 획득 과금 업체는 <b>가입 혜택 메시지가 메시지 항목에서 빠지고 획득 단가에 포함</b>된다. 대시보드 '메시지 발송'에는 가입 혜택 메시지도 들어가므로
                  그만큼 더 많게 나온다. 획득 수량은 <b>가입 고객 수</b>라 가입 혜택 메시지 수와 1:1 이 아닐 수 있다.</>
              )}
            </div>
          </div>

          <div className="overflow-x-auto border rounded-lg">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-100">
                <tr>
                  {['분류', '유형', '항목명', '단가(공급가)', '수량', '공급가액', '면세', '이용월/일할', '선/후불', '수동', ''].map((h) => (
                    <th key={h} className="px-2 py-1 text-left whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {items.map((it, idx) => (
                  <tr key={idx} className="border-t">
                    <td className="px-2 py-1"><input className="border rounded px-1 w-24" value={it.category} disabled={!isDraft}
                        onChange={(e) => editItem(idx, 'category', e.target.value)} /></td>
                    <td className="px-2 py-1"><input className="border rounded px-1 w-28" value={it.itemType} disabled={!isDraft}
                        onChange={(e) => editItem(idx, 'itemType', e.target.value)} /></td>
                    <td className="px-2 py-1"><input className="border rounded px-1 w-40" value={it.name} disabled={!isDraft}
                        onChange={(e) => editItem(idx, 'name', e.target.value)} /></td>
                    <td className="px-2 py-1 text-right"><input type="number" className="border rounded px-1 w-24 text-right" value={it.unitPriceNet} disabled={!isDraft}
                        onChange={(e) => editItem(idx, 'unitPriceNet', e.target.value)} /></td>
                    <td className="px-2 py-1 text-right"><input type="number" className="border rounded px-1 w-20 text-right" value={it.quantity} disabled={!isDraft}
                        onChange={(e) => editItem(idx, 'quantity', e.target.value)} /></td>
                    <td className="px-2 py-1 text-right"><input type="number" className="border rounded px-1 w-28 text-right" value={it.supplyAmountNet} disabled={!isDraft}
                        onChange={(e) => editItem(idx, 'supplyAmountNet', e.target.value)} /></td>
                    <td className="px-2 py-1 text-center"><input type="checkbox" checked={!!it.vatExempt} disabled={!isDraft}
                        onChange={(e) => editItem(idx, 'vatExempt', e.target.checked)} /></td>
                    <td className="px-2 py-1 whitespace-nowrap">
                      {it.coverageFrom && it.coverageTo ? (
                        <span className="text-xs">
                          <span className="px-1 py-0.5 rounded bg-amber-100 text-amber-800 mr-1">일할</span>
                          {it.coverageFrom}~{it.coverageTo}
                        </span>
                      ) : (it.coverageMonth || '-')}
                    </td>
                    <td className="px-2 py-1">{it.billingMode}</td>
                    <td className="px-2 py-1 text-center">{it.isManual ? '✔' : ''}</td>
                    <td className="px-2 py-1">{isDraft && (
                      <button className="text-red-500" onClick={() => removeItem(idx)}>삭제</button>
                    )}</td>
                  </tr>
                ))}
                {items.length === 0 && (
                  <tr><td colSpan={11} className="px-2 py-4 text-center text-gray-400">항목 없음</td></tr>
                )}
              </tbody>
              <tfoot className="bg-gray-50 font-semibold">
                <tr className="border-t">
                  <td colSpan={5} className="px-2 py-1 text-right">미리보기 합계(저장 시 서버 재계산)</td>
                  <td className="px-2 py-1 text-right">{won(preview.supplyAll)}</td>
                  <td colSpan={5} className="px-2 py-1 text-xs text-gray-500">
                    과세 {won(preview.taxable)} · VAT {won(preview.vat)} · 합계 {won(preview.total)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>

          {isDraft && (
            <div className="flex gap-2">
              <button className="bg-gray-200 px-3 py-1.5 rounded" onClick={addItem}>+ 항목 추가</button>
              <button className="bg-blue-600 text-white px-3 py-1.5 rounded disabled:opacity-50" onClick={doSaveItems} disabled={loading}>항목 저장</button>
            </div>
          )}

          {proratedItems.length > 0 && (
            <div className="border border-amber-200 bg-amber-50 rounded-lg p-3 text-sm space-y-2">
              <div className="font-medium text-amber-800">일할 계산된 항목 {proratedItems.length}건 (첫 유료일 기준 개시월 일할)</div>
              <pre className="text-xs whitespace-pre-wrap text-gray-700 font-sans">{prorationNote}</pre>
              {isDraft && (
                <button className="text-xs bg-amber-600 text-white px-2 py-1 rounded" onClick={appendProrationToRemark}>
                  일할 내역 고객 노출 비고에 추가
                </button>
              )}
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <label className="flex flex-col text-sm">
              <span>
                고객 노출 비고
                {remarkDirty && (
                  <span className="ml-2 px-1.5 py-0.5 rounded text-xs bg-amber-100 text-amber-700">
                    저장 안 됨 — 확정 시 함께 저장됩니다
                  </span>
                )}
              </span>
              <textarea className="border rounded px-2 py-1 h-20" value={customerRemark} disabled={!isDraft}
                        onChange={(e) => setCustomerRemark(e.target.value)} />
            </label>
            <label className="flex flex-col text-sm">관리자 메모(내부)
              <textarea className="border rounded px-2 py-1 h-20" value={adminMemo} disabled={!isDraft}
                        onChange={(e) => setAdminMemo(e.target.value)} />
            </label>
          </div>

          <div className="flex flex-wrap gap-2">
            {isDraft && <button className="bg-blue-600 text-white px-3 py-1.5 rounded disabled:opacity-50" onClick={doSaveRemark} disabled={loading}>비고 저장</button>}
            {isDraft && <button className="bg-green-600 text-white px-3 py-1.5 rounded disabled:opacity-50" onClick={doConfirmAndRefresh} disabled={loading}>확정</button>}
            {!isDraft && settlement.status === 'CONFIRMED' && (
              <button className="bg-orange-500 text-white px-3 py-1.5 rounded disabled:opacity-50" onClick={doReopenAndRefresh} disabled={loading}>재오픈(정정)</button>
            )}
          </div>

          {/* ---------- 정산 이력(이 채널) — 전월 금액과 견주기 (2026-10-01) ---------- */}
          {(() => {
            const prev = prevMonthOf(settlement.billingMonth);
            const prevRow = history.find((h) => h.billingMonth === prev);
            const diff = prevRow ? (settlement.total ?? 0) - (prevRow.total ?? 0) : null;
            const pct = prevRow && prevRow.total ? Math.round((diff / prevRow.total) * 1000) / 10 : null;
            return (
              <div className="border rounded-lg">
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1 bg-gray-50 border-b px-4 py-2 text-sm">
                  <span className="font-semibold text-gray-800">정산 이력</span>
                  {prevRow ? (
                    <span className="text-gray-600">
                      전월({prev}) 합계 <b>{won(prevRow.total)}</b> → 이번({settlement.billingMonth}) 합계 <b>{won(settlement.total)}</b>
                      <span className={`ml-2 font-semibold ${diff > 0 ? 'text-red-600' : diff < 0 ? 'text-blue-600' : 'text-gray-500'}`}>
                        {diff > 0 ? '+' : ''}{won(diff)}{pct !== null ? ` (${pct > 0 ? '+' : ''}${pct}%)` : ''}
                      </span>
                    </span>
                  ) : (
                    <span className="text-gray-400">전월({prev}) 정산서 없음</span>
                  )}
                  <span className="text-xs text-gray-400">이번 합계는 저장된 값 기준(항목을 고친 뒤 저장 전이면 위 미리보기 합계를 볼 것) · 행을 누르면 그 달 항목</span>
                </div>
                <div className="p-3 overflow-x-auto">
                  {historyErr
                    ? <div className="text-sm text-red-600">정산 이력을 불러오지 못했습니다: {historyErr}</div>
                    : <SettlementHistoryTable channelId={historyChannelId} history={history}
                                              highlightMonth={settlement.billingMonth} compareMonth={prev} />}
                </div>
              </div>
            );
          })()}
        </div>
      )}

      {/* ===== '결제 실패' 호버 툴팁 =====
          목록 스크롤 컨테이너에 잘리지 않도록 **화면 기준(fixed)** 으로 띄운다.
          `pointer-events-none` — 툴팁이 커서를 가로채면 곧바로 mouseleave 가 떠서 깜빡인다. */}
      {failTip && (
        <div
          className="fixed z-[60] w-[340px] rounded-lg bg-gray-900 text-white text-xs px-3 py-2.5 shadow-2xl pointer-events-none leading-relaxed"
          style={{
            left: failTip.left,
            top: failTip.top,
            transform: failTip.flip ? 'translateY(-100%)' : 'none',
          }}
        >
          <div className="font-bold text-red-300">
            최근 결제 실패 · {dateTimeText(failTip.row.lastPaymentFailedAt) || '시각 미상'}
          </div>
          <div className="mt-1 break-words">
            사유: {failTip.row.lastPaymentError || '기록 없음(슬랙 결제 알림 확인)'}
          </div>
          {failTip.row.paymentFailCount > 1 && (
            <div className="mt-1 text-gray-300">누적 실패 {failTip.row.paymentFailCount}회</div>
          )}
          <div className="mt-1.5 text-gray-400">'결제 실행'으로 다시 시도할 수 있습니다.</div>
        </div>
      )}

      {/* ===== 카드 결제 확인 팝업 =====
          [PAYMENT_RISK] 이 팝업의 '결제 실행'을 누르는 순간 고객 카드에 **실제 승인**이 올라간다.
          legacy 빌링의 '수동 결제' 팝업과 같은 자리에 있는 마지막 사람 확인 단계다. */}
      {payTarget && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden">
            <div className="px-6 py-4 border-b flex items-center justify-between">
              <span className="font-bold text-indigo-700">카드 결제 실행</span>
              <button className="text-gray-400 hover:text-gray-600 text-lg leading-none"
                      onClick={() => setPayTarget(null)}>✕</button>
            </div>

            <div className="px-6 py-5">
              <div className="text-sm text-gray-500">
                #{payTarget.row.channelId} · {month} 청구분
              </div>
              <div className="text-lg font-bold text-gray-900 mb-4">{payTarget.row.channelName}</div>

              <div className="bg-gray-50 rounded-xl p-4 max-h-56 overflow-y-auto">
                <div className="text-xs font-bold text-gray-400 mb-2">청구 항목 내역</div>
                {payTarget.loading && <div className="text-sm text-gray-400 py-2">내역 불러오는 중…</div>}
                {!payTarget.loading && !payTarget.settlement && (
                  <div className="text-sm text-gray-400 py-2">내역을 불러오지 못했습니다. 총액만 확인하고 진행합니다.</div>
                )}
                {payTarget.settlement?.items?.map((it) => (
                  <div key={it.id} className="flex justify-between text-sm py-0.5">
                    <span className="text-gray-600 pr-3">
                      {it.name}
                      {it.vatExempt && <span className="ml-1 text-xs text-gray-400">(면세)</span>}
                    </span>
                    <span className={`font-medium whitespace-nowrap ${it.supplyAmountNet < 0 ? 'text-red-600' : 'text-gray-900'}`}>
                      {won(it.supplyAmountNet)}원
                    </span>
                  </div>
                ))}
                {payTarget.settlement && (
                  <div className="border-t mt-2 pt-2 text-sm space-y-0.5">
                    <div className="flex justify-between text-gray-500">
                      <span>공급가</span><span>{won(payTarget.settlement.supplyTotal)}원</span>
                    </div>
                    <div className="flex justify-between text-gray-500">
                      <span>부가세</span><span>{won(payTarget.settlement.vat)}원</span>
                    </div>
                  </div>
                )}
              </div>

              <div className="flex items-center justify-between mt-4 px-1">
                <span className="font-bold text-gray-800">최종 결제 금액</span>
                <span className="text-2xl font-black text-indigo-600">
                  {won(payTarget.settlement?.total ?? payTarget.row.total)}<span className="text-base font-bold"> 원</span>
                </span>
              </div>

              {/* 이 모달을 열기 전에 이미 실패한 이력이 있으면 먼저 알려준다 — 같은 사유로 또 시도하지 않도록. */}
              {!payTarget.error && payTarget.row.lastPaymentFailedAt && (
                <div className="mt-4 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3">
                  <div className="text-sm font-bold text-amber-800">
                    직전 결제가 실패한 건입니다{payTarget.row.paymentFailCount > 1 ? ` (누적 ${payTarget.row.paymentFailCount}회)` : ''}
                  </div>
                  <div className="text-xs text-amber-800 mt-1">
                    {dateTimeText(payTarget.row.lastPaymentFailedAt)} · {payTarget.row.lastPaymentError || '사유 기록 없음'}
                  </div>
                </div>
              )}

              {/* 결제 실패 사유. 실패해도 서버는 아무것도 바꾸지 않으므로 사유만 고치면 그대로 재시도할 수 있다. */}
              {payTarget.error && (
                <div className="mt-4 rounded-xl border border-red-300 bg-red-50 px-4 py-3">
                  <div className="text-sm font-bold text-red-700">결제에 실패했습니다</div>
                  <div className="text-sm text-red-700 mt-1 whitespace-pre-line break-words">{payTarget.error}</div>
                  <div className="text-xs text-red-500 mt-2">
                    수납 처리는 되지 않았습니다. 카드 상태를 확인한 뒤 다시 시도하거나, 계좌 입금이면 '입금 확인'으로 마감하세요.
                  </div>
                </div>
              )}

              <p className="text-xs text-gray-500 mt-4 leading-relaxed">
                고객이 등록한 <b>기본 결제 카드</b>로 즉시 청구됩니다. 성공하면 이 정산서는 <b>수납 완료</b>로 마감되고,
                실패하면 아무것도 변경되지 않습니다.<br />
                금액은 확정된 정산서 금액이라 이 화면에서 바꿀 수 없습니다 — 정정하려면 <b>재오픈 → 항목 수정 → 재확정</b>을 거치세요.
              </p>
            </div>

            <div className="px-6 py-4 bg-gray-50 flex gap-3">
              <button className="flex-1 py-3 text-sm font-bold text-gray-500 hover:bg-gray-100 rounded-xl disabled:opacity-50"
                      onClick={() => setPayTarget(null)} disabled={paying}>닫기</button>
              <button className="flex-[2] py-3 bg-indigo-600 text-white rounded-xl font-bold hover:bg-indigo-700 disabled:opacity-50"
                      onClick={doPayCard} disabled={paying || loading || payTarget.loading}>
                {paying
                  ? '결제 요청 중…'
                  : `${won(payTarget.settlement?.total ?? payTarget.row.total)}원 결제 ${payTarget.error ? '다시 시도' : '실행'}`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
